'use client';
import { formatSpeed } from '../../lib/displayFormat';

import { useState, useEffect, useCallback, type ReactNode } from 'react';
import { BarChart3, ChevronLeft, ChevronRight, Database, DollarSign, Boxes, ArrowDownToLine, ArrowUpFromLine, Zap, Gauge, type LucideIcon } from 'lucide-react';
import { useBrainChat, useBrainChatStatus } from './BrainChatProvider';
import { useBrainContextUsage, useModelUsage } from '../../lib/queries';
import { useTranslation } from '../../lib/i18n';
import { formatUsageCount, formatUsd } from '../../lib/format';
import { buildUsageSummary } from '../../lib/usageBars';
import { cacheHitPct, contextMeter } from '../../lib/displayFormat';
import { usageProgressColour } from '../../lib/usageMeter';
import { ModelIcon } from '../../components/ui/ModelIcon';
import { ToolMark } from '../../components/ui/ToolMark';
import { LoadingState, ErrorState, EmptyState } from '../../components/ui/states';
import { Modal, ModalBody } from '../../components/ui/Modal';
import { Button } from '../../components/ui/Button';
import { brainModelLabel, brainModelQualifiedLabel } from '../../lib/modelProvider';

const SECTIONS = ['conversation', 'models', 'context'] as const;
type Section = (typeof SECTIONS)[number];

function StatTile({ label, icon: Icon, children }: { label: string; icon?: LucideIcon; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1 rounded-md border border-border bg-muted px-3 py-2">
      <span className={Icon ? 'flex items-center gap-1.5 text-small text-muted-foreground' : 'text-small text-muted-foreground'}>
        {Icon ? <Icon size={13} aria-hidden /> : null}
        {label}
      </span>
      <span className="text-ui tabular-nums text-foreground">{children}</span>
    </div>
  );
}

export function StatsModal({ onClose }: { onClose: () => void }) {
  const { t, locale } = useTranslation();
  const { currentModel, provider, providerLabel, activeSessionId } = useBrainChat();
  const { usage } = useBrainChatStatus();
  const usageQuery = useModelUsage();
  const summary = buildUsageSummary(usageQuery.data, locale);

  const [section, setSection] = useState<Section>('conversation');
  // Only while the Context section is on screen: the breakdown walks the live transcript server-side.
  const contextQuery = useBrainContextUsage(activeSessionId, section === 'context');
  const breakdown = contextQuery.data ?? null;

  const cycle = useCallback((dir: -1 | 1) => {
    setSection((cur) => SECTIONS[(SECTIONS.indexOf(cur) + dir + SECTIONS.length) % SECTIONS.length] ?? cur);
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') { e.preventDefault(); cycle(-1); }
      if (e.key === 'ArrowRight') { e.preventDefault(); cycle(1); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [cycle]);

  const u = usage;
  const context = contextMeter(u?.percent);
  const pct = context.percent != null ? Math.round(context.percent) : null;
  const convCacheHit = u ? cacheHitPct({ cacheRead: u.cacheRead, cacheWrite: u.cacheWrite, input: u.input }) : null;

  // `inspect`: a read-only report. Nothing here is edited, so a phone shows it as a bottom sheet.
  return (
    <Modal title={t.stats.modalTitle} onClose={onClose} size="md" icon={BarChart3}>
      <ModalBody gap={4}>
        {/* Section pager */}
        <div className="flex items-center justify-between gap-3">
          <Button
            variant="ghost" size="icon"
            type="button"
            onClick={() => cycle(-1)}
            aria-label={t.common.back}
            className="size-7"
          >
            <ChevronLeft size={16} aria-hidden />
          </Button>

          <div className="flex items-center gap-2 text-section-title text-foreground">
            {section === 'conversation' ? t.stats.sectionConversation : section === 'models' ? t.stats.sectionModels : t.stats.sectionContext}
            <span className="text-small text-muted-foreground">
              {`${SECTIONS.indexOf(section) + 1}/${SECTIONS.length}`}
            </span>
          </div>

          <Button
            variant="ghost" size="icon"
            type="button"
            onClick={() => cycle(1)}
            aria-label={t.common.forward}
            className="size-7"
          >
            <ChevronRight size={16} aria-hidden />
          </Button>
        </div>

        {section === 'conversation' && (
          <div className="flex flex-col gap-4">
            {/* Context usage bar */}
            <div className="flex flex-col gap-1.5">
              <span className="text-small text-muted-foreground">{t.stats.contextLabel}</span>
              <div className="relative h-6 w-full overflow-hidden rounded-md border border-border bg-muted">
                <div
                  className="h-full rounded-md transition-all"
                  style={{
                    width: `${Math.min(100, pct ?? 0)}%`,
                    background: usageProgressColour(pct ?? 0),
                  }}
                />
              </div>
              <span className="text-small tabular-nums text-muted-foreground">
                {u ? `${u.tokens == null ? '—' : formatUsageCount(u.tokens, locale)} / ${formatUsageCount(u.contextWindow, locale)}` : '—'}
                {pct != null ? `  ·  ${pct} %` : ''}
              </span>
            </div>

            {/* Model row */}
            <div className="flex items-center justify-between rounded-md border border-border bg-muted px-3 py-2">
              <span className="text-small text-muted-foreground">{t.stats.model}</span>
              <span
                className="text-ui text-foreground"
                title={currentModel ? brainModelQualifiedLabel({ provider, providerLabel, model: currentModel }) : undefined}
              >
                {currentModel ? brainModelLabel({ model: currentModel }) : '—'}
              </span>
            </div>

            {/* Metrics grid */}
            <div className="grid grid-cols-2 gap-3">
              <StatTile label={t.stats.sessionTokens} icon={BarChart3}>
                {u ? formatUsageCount(u.totalTokens, locale) : '—'}
              </StatTile>
              <StatTile label={t.stats.cardTotalCost} icon={DollarSign}>
                {u ? formatUsd(u.cost, locale, 4) : '—'}
              </StatTile>
              <StatTile label={t.stats.inputTokens} icon={ArrowDownToLine}>
                {u?.input != null ? formatUsageCount(u.input, locale) : '—'}
              </StatTile>
              <StatTile label={t.stats.outputTokens} icon={ArrowUpFromLine}>
                {u?.output != null ? formatUsageCount(u.output, locale) : '—'}
              </StatTile>
              <StatTile label={t.stats.cacheHit} icon={Zap}>
                {convCacheHit != null ? `${Math.round(convCacheHit)} %` : '—'}
              </StatTile>
              {/* Current-turn generated output, including reasoning and tool calls, over successful
                  provider generation time. Queue/header wait, retries and tools are excluded. */}
              <StatTile label={t.stats.speed} icon={Gauge}>
                {u ? formatSpeed(u.effectiveTps, locale) : '—'}
              </StatTile>
            </div>

            <p className="text-meta text-muted-foreground">{t.stats.arrowHint}</p>
          </div>
        )}

        {section === 'models' && (
          <div className="flex flex-col gap-3">
            {/* Totals strip */}
            {summary.hasAnyUsage && (
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <StatTile label={t.stats.cardTotalTokens} icon={BarChart3}>
                  {summary.totalTokensLabel}
                </StatTile>
                <StatTile label={t.stats.cardCache} icon={Database}>
                  {summary.totalCacheLabel}
                </StatTile>
                <StatTile label={t.stats.cardModelsUsed} icon={Boxes}>
                  {summary.modelsUsed}
                </StatTile>
              </div>
            )}

            {/* Per-model rows */}
            {usageQuery.isLoading ? (
              <LoadingState variant="list" />
            ) : usageQuery.isError ? (
              <ErrorState message={t.common.daemonUnreachable} onRetry={() => usageQuery.refetch()} />
            ) : !summary.hasAnyUsage ? (
              <EmptyState title={t.stats.emptyTitle} description={t.stats.emptyDesc} icon={BarChart3} />
            ) : (
              <div className="flex flex-col gap-px overflow-hidden rounded-md border border-border bg-border/50">
                {summary.rows.map((row) => (
                  <div
                    key={row.exec}
                    className="flex items-center gap-2 bg-card px-3 py-2"
                    title={row.cacheHitPct != null ? `${t.stats.cacheHit}: ${Math.round(row.cacheHitPct)} %` : undefined}
                  >
                    <ModelIcon name={row.exec} size={15} />
                    <span className="min-w-0 flex-1 truncate text-card-title text-foreground" title={row.exec}>
                      {row.exec}
                    </span>
                    <div className="hidden h-px w-16 flex-1 sm:block">
                      <div
                        className="h-px rounded-full"
                        style={{
                          width: `${row.pct}%`,
                          // The heat ramp end to end: accent → ember → the pale ember stop. All three
                          // are tokens, so the bar follows a repaint instead of staying orange under a
                          // skin that moved the accent out from under it.
                          background: 'linear-gradient(90deg, var(--color-primary), var(--color-ember), var(--color-ember-bright))',
                        }}
                      />
                    </div>
                    <span className="text-small tabular-nums text-muted-foreground">
                      {row.tokensLabel}
                    </span>
                    <span className="w-20 text-right text-small tabular-nums text-foreground">
                      {row.costLabel}
                    </span>
                  </div>
                ))}
              </div>
            )}

            <p className="text-meta text-muted-foreground">{t.stats.arrowHint}</p>
          </div>
        )}

        {section === 'context' && (
          <div className="flex flex-col gap-4">
            {contextQuery.isLoading ? (
              <LoadingState variant="list" />
            ) : contextQuery.isError ? (
              <ErrorState message={t.common.daemonUnreachable} onRetry={() => contextQuery.refetch()} />
            ) : !breakdown ? (
              <EmptyState title={t.stats.contextEmptyTitle} description={t.stats.contextEmptyDesc} icon={BarChart3} />
            ) : (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <StatTile label={t.stats.contextWindow}>
                    {formatUsageCount(breakdown.contextWindow, locale)}
                  </StatTile>
                  <StatTile label={t.stats.contextReported}>
                    {breakdown.reportedTokens != null ? formatUsageCount(breakdown.reportedTokens, locale) : '—'}
                  </StatTile>
                </div>

                <div className="flex flex-col gap-2">
                  <div className="flex items-baseline justify-between gap-3">
                    <h3 className="text-section-title text-muted-foreground">{t.stats.contextBreakdown}</h3>
                    {breakdown.compactAtTokens != null ? (
                      <span className="text-small tabular-nums text-muted-foreground">
                        {t.stats.compactsAt}: {formatUsageCount(breakdown.compactAtTokens, locale)}
                      </span>
                    ) : null}
                  </div>
                  {breakdown.categories.map((category) => (
                    <ContextBar
                      key={category.id}
                      locale={locale}
                      label={t.stats.contextCategory[category.id]}
                      tokens={category.tokens}
                      percent={category.percent}
                    />
                  ))}
                  <ContextBar label={t.stats.contextCategoryFree} tokens={breakdown.free.tokens} percent={breakdown.free.percent} locale={locale} muted />
                  <p className="text-meta text-muted-foreground">{t.stats.contextEstimateNote}</p>
                </div>

                {breakdown.tools.length > 0 ? (
                  <div className="flex flex-col gap-2">
                    <h3 className="text-section-title text-muted-foreground">{t.stats.heaviestTools}</h3>
                    <div className="flex flex-col gap-px overflow-hidden rounded-md border border-border bg-border/50">
                      {breakdown.tools.map((tool) => (
                        <div key={tool.name} className="flex items-center gap-2 bg-card px-3 py-2">
                          <ToolMark name={tool.name} />
                          <span className="min-w-0 flex-1 truncate font-mono text-card-title text-foreground" title={tool.name}>{tool.name}</span>
                          <span className="hidden text-small tabular-nums text-muted-foreground sm:inline">
                            {`${formatUsageCount(tool.schemaTokens, locale)} · ${formatUsageCount(tool.callTokens, locale)} · ${formatUsageCount(tool.resultTokens, locale)}`}
                          </span>
                          <span className="w-16 text-right text-small tabular-nums text-foreground">{formatUsageCount(tool.tokens, locale)}</span>
                        </div>
                      ))}
                    </div>
                    <p className="text-meta text-muted-foreground">{t.stats.toolColumnsHint}</p>
                  </div>
                ) : null}
              </>
            )}

            <p className="text-meta text-muted-foreground">{t.stats.arrowHint}</p>
          </div>
        )}
      </ModalBody>
    </Modal>
  );
}

/** One category of the context breakdown: label, share bar, tokens. Same bar language as the conversation
 *  section's context meter, just slimmer — several of them stack. */
function ContextBar({ label, tokens, percent, locale, muted }: { label: string; tokens: number; percent: number; locale: string; muted?: boolean }) {
  return (
    <div className="flex items-center gap-3">
      <span className="w-24 shrink-0 truncate text-small text-muted-foreground" title={label}>{label}</span>
      <div className="relative h-2 flex-1 overflow-hidden rounded-full border border-border bg-muted">
        <div
          className="h-full rounded-full transition-all"
          style={{ width: `${Math.min(100, Math.max(0, percent))}%`, background: muted ? 'var(--color-border)' : 'var(--color-primary)' }}
        />
      </div>
      <span className="w-14 text-right text-small tabular-nums text-foreground">{formatUsageCount(tokens, locale)}</span>
      <span className="w-10 text-right text-small tabular-nums text-muted-foreground">{`${Math.round(percent)} %`}</span>
    </div>
  );
}
